import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import {
  TrendingUp, TrendingDown, DollarSign, Target, AlertTriangle, ArrowRight,
  Trophy, Layers, Sparkles, Clock, Zap
} from "lucide-react";

const CURRENCY_SYMBOLS = { CLP: "$", USD: "US$", EUR: "€", UF: "UF " };
const fmtCurrency = (cur, val) => {
  const sym = CURRENCY_SYMBOLS[cur] || `${cur} `;
  const opts = cur === "CLP" ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 0, maximumFractionDigits: 2 };
  return `${sym}${Number(val || 0).toLocaleString("es-CL", opts)}`;
};

function CurrencyList({ data, emptyLabel = "Sin datos" }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <div className="text-slate-400 text-sm">{emptyLabel}</div>;
  return (
    <div className="space-y-1">
      {entries.map(([cur, item]) => (
        <div key={cur} className="flex items-baseline gap-2">
          <span className="text-2xl lg:text-3xl font-black text-slate-900 tracking-tight">
            {fmtCurrency(cur, typeof item === "object" ? item.value : item)}
          </span>
          {typeof item === "object" && (
            <span className="text-xs text-slate-400 font-medium">· {item.count}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function KpiCard({ icon: Icon, label, children, accent = "blue", helper }) {
  const colors = {
    blue: "text-blue-600 bg-blue-50",
    emerald: "text-emerald-600 bg-emerald-50",
    violet: "text-violet-600 bg-violet-50",
    amber: "text-amber-600 bg-amber-50",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[accent]}`}>
          <Icon size={18} />
        </div>
        {helper}
      </div>
      <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">{label}</div>
      {children}
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const maxFunnelCount = useMemo(() => {
    if (!data?.funnel) return 1;
    return Math.max(1, ...data.funnel.map((f) => f.count));
  }, [data]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  if (!data) return <div className="text-slate-500">No se pudo cargar el dashboard.</div>;

  const { kpis, funnel, stalled, top_clients } = data;
  const conversion = kpis.conversion_rate;
  const trendIcon = conversion >= 50 ? <TrendingUp size={14} className="text-emerald-500" /> : <TrendingDown size={14} className="text-amber-500" />;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black font-['Satoshi'] tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1 text-sm">Foto rápida de tu operación comercial · {new Date().toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
        <button
          onClick={() => navigate("/app/pipeline")}
          className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-slate-800 transition-all active:scale-95"
          data-testid="dashboard-open-pipeline"
        >
          <Zap size={15} /> Ir al Pipeline
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={DollarSign} label="Cerrado este mes" accent="emerald">
          <CurrencyList data={kpis.closed_month} emptyLabel="Sin cierres aún" />
        </KpiCard>
        <KpiCard icon={Layers} label="Valor en pipeline" accent="blue">
          <CurrencyList data={kpis.pipeline_value} emptyLabel="Pipeline vacío" />
        </KpiCard>
        <KpiCard icon={Target} label="Ticket promedio" accent="violet">
          {Object.keys(kpis.avg_ticket).length === 0 ? (
            <div className="text-slate-400 text-sm">Sin cerradas históricas</div>
          ) : (
            <div className="space-y-1">
              {Object.entries(kpis.avg_ticket).map(([cur, val]) => (
                <div key={cur} className="text-2xl lg:text-3xl font-black text-slate-900 tracking-tight">{fmtCurrency(cur, val)}</div>
              ))}
            </div>
          )}
        </KpiCard>
        <KpiCard icon={Trophy} label="Tasa de conversión" accent="amber" helper={trendIcon}>
          <div className="text-3xl font-black text-slate-900 tracking-tight">{conversion}%</div>
          <div className="text-xs text-slate-500 mt-1">
            {kpis.accepted_total} aceptadas · {kpis.rejected_total} rechazadas
          </div>
        </KpiCard>
      </div>

      {/* Requiere atención + Embudo */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Stalled */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stalled.length > 0 ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-400"}`}>
                <AlertTriangle size={16} />
              </div>
              <h2 className="font-bold text-slate-900">Requiere atención</h2>
            </div>
            <span className="text-xs text-slate-400">{"> 7 días sin cambio"}</span>
          </div>
          {stalled.length === 0 ? (
            <div className="p-8 text-center">
              <Sparkles size={28} className="mx-auto text-emerald-300 mb-2" />
              <p className="text-sm text-slate-500">Sin cotizaciones estancadas. ¡Bien ahí!</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
              {stalled.map((s) => (
                <li
                  key={s.id}
                  className="px-5 py-3 hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-3"
                  onClick={() => navigate("/app/pipeline")}
                  data-testid={`stalled-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 text-sm truncate">{s.title}</div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border"
                        style={{ backgroundColor: `${s.status_color}15`, color: s.status_color, borderColor: `${s.status_color}40` }}
                      >
                        {s.status_name}
                      </span>
                      <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                        <Clock size={11} /> {s.days_stalled}d
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-slate-900">{fmtCurrency(s.currency, s.total)}</div>
                    <ArrowRight size={14} className="text-slate-300 ml-auto mt-1" />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Funnel */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
                <Layers size={16} />
              </div>
              <h2 className="font-bold text-slate-900">Embudo del pipeline</h2>
            </div>
            <button
              onClick={() => navigate("/app/pipeline")}
              className="text-xs text-blue-600 font-semibold hover:underline"
              data-testid="funnel-goto-pipeline"
            >
              Ver detalle →
            </button>
          </div>
          <div className="p-5 space-y-3">
            {funnel.map((s) => {
              const pct = maxFunnelCount > 0 ? (s.count / maxFunnelCount) * 100 : 0;
              return (
                <div key={s.id} data-testid={`funnel-stage-${s.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="font-medium text-slate-700">{s.name}</span>
                      <span className="text-slate-400">· {s.count}</span>
                    </div>
                    <div className="text-xs text-slate-500 font-medium text-right">
                      {Object.entries(s.value_by_currency).length === 0 ? "—" :
                        Object.entries(s.value_by_currency).map(([c, v]) => fmtCurrency(c, v)).join(" · ")}
                    </div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(pct, s.count > 0 ? 6 : 0)}%`, backgroundColor: s.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top clientes */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-violet-50 text-violet-600">
              <Trophy size={16} />
            </div>
            <h2 className="font-bold text-slate-900">Top 5 clientes por volumen cotizado</h2>
          </div>
          <button onClick={() => navigate("/app/clients")} className="text-xs text-blue-600 font-semibold hover:underline" data-testid="top-goto-clients">Ver clientes →</button>
        </div>
        {top_clients.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Aún no hay clientes con cotizaciones.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {top_clients.map((c, i) => (
              <li key={c.client_id} className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors" data-testid={`top-client-${c.client_id}`}>
                <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 font-black text-sm flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{c.client_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.count} {c.count === 1 ? "cotización" : "cotizaciones"}</div>
                </div>
                <div className="text-right text-sm font-semibold text-slate-900">
                  {Object.entries(c.value_by_currency).length === 0 ? "—" :
                    Object.entries(c.value_by_currency).map(([cur, v]) => (
                      <div key={cur}>{fmtCurrency(cur, v)}</div>
                    ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
